// Global user-timezone-aware time formatting. Zone comes from profile (synced via
// /auth/me) or auto-detected from the browser. All formatters append the short
// zone code, e.g. "12:56 IST".
const KEY = "kairos_tz";
let userTz = "";
try { userTz = localStorage.getItem(KEY) || ""; } catch { /* private mode */ }

export const detectTz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
};
export const getUserTimezone = () => userTz || detectTz();
export const setUserTimezone = (tz) => {
  userTz = tz || "";
  try { tz ? localStorage.setItem(KEY, tz) : localStorage.removeItem(KEY); } catch { /* ignore */ }
  setTimeout(() => { try { window.dispatchEvent(new Event("kairos-tz")); } catch { /* ignore */ } }, 0);
};
export const syncUserTimezone = (tz) => {
  const v = tz || "";
  if (lastServerTz === v) return;
  lastServerTz = v;
  if (v !== userTz) setUserTimezone(v);
};
let lastServerTz = null;
const ALIASES = ["Asia/Kolkata", "Asia/Saigon", "Asia/Rangoon", "Europe/Kiev", "America/Buenos_Aires"];
export const allTimezones = () => {
  try {
    const set = new Set(Intl.supportedValuesOf("timeZone"));
    ALIASES.forEach((z) => set.add(z));
    return [...set].sort();
  } catch { return ["UTC"]; }
};

// Friendly abbreviations for fixed-offset zones Intl renders as "GMT+X".
export const ZONE_ABBR = {
  "Asia/Kolkata": "IST", "Asia/Calcutta": "IST", "Asia/Colombo": "IST",
  "Asia/Qatar": "AST", "Asia/Riyadh": "AST", "Asia/Kuwait": "AST", "Asia/Bahrain": "AST", "Asia/Baghdad": "AST",
  "Asia/Dubai": "GST", "Asia/Muscat": "GST",
  "Asia/Karachi": "PKT", "Asia/Dhaka": "BST", "Asia/Kathmandu": "NPT",
  "Asia/Manila": "PHT", "Asia/Singapore": "SGT", "Asia/Hong_Kong": "HKT",
  "Asia/Tokyo": "JST", "Asia/Seoul": "KST", "Asia/Jakarta": "WIB",
  "Asia/Tehran": "IRST", "Asia/Kabul": "AFT", "Asia/Tashkent": "UZT", "Europe/Istanbul": "TRT",
};

const cache = {};
const dtfIn = (tz, opts, locale = "en-GB") => {
  const k = `${locale}|${tz}|${JSON.stringify(opts)}`;
  return cache[k] || (cache[k] = new Intl.DateTimeFormat(locale, { timeZone: tz, ...opts }));
};
const D = (v) => (v instanceof Date ? v : new Date(v));

export const tzAbbrevIn = (tz, d = new Date()) => {
  if (ZONE_ABBR[tz]) return ZONE_ABBR[tz];
  try {
    for (const loc of ["en-US", "en-GB", "en-AU"]) {
      const p = dtfIn(tz, { hour: "2-digit", minute: "2-digit", timeZoneName: "short" }, loc)
        .formatToParts(D(d)).find((x) => x.type === "timeZoneName");
      const v = p ? p.value.replace("UTC", "GMT").replace(/\s/g, "") : "";
      if (v && !v.startsWith("GMT")) return v;
      if (loc === "en-AU") return v;
    }
  } catch { /* fall through */ }
  return "";
};
export const tzAbbrev = (d = new Date()) => tzAbbrevIn(getUserTimezone(), d);

export const fmtHMIn = (tz, d = new Date()) =>
  `${dtfIn(tz, { hour: "2-digit", minute: "2-digit", hour12: false }).format(D(d))} ${tzAbbrevIn(tz, d)}`;

// "12:56" (no abbrev)
export const fmtHMPlain = (iso) =>
  iso ? dtfIn(getUserTimezone(), { hour: "2-digit", minute: "2-digit", hour12: false }).format(D(iso)) : "—";
// "12:56 IST"
export const fmtHM = (iso) => (iso ? `${fmtHMPlain(iso)} ${tzAbbrev(D(iso))}` : "—");
// "12:56–13:10 IST"
export const fmtHMRange = (a, b) =>
  a ? `${fmtHMPlain(a)}${b ? `–${fmtHMPlain(b)}` : ""} ${tzAbbrev(D(a))}` : "—";
// "12:56:03 IST"
export const fmtHMS = (iso) =>
  iso ? `${dtfIn(getUserTimezone(), { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(D(iso))} ${tzAbbrev(D(iso))}` : "—";
// date-only, user zone (default "Thu, Jun 5")
export const fmtDay = (iso, opts = { weekday: "short", month: "short", day: "numeric" }) =>
  iso ? dtfIn(getUserTimezone(), opts, "en-US").format(D(iso)) : "";
// "05 Jun, 12:56 IST"
export const fmtDT = (iso) =>
  iso ? `${dtfIn(getUserTimezone(), { day: "2-digit", month: "short" }).format(D(iso))}, ${fmtHM(iso)}` : "—";
// "05 Jun, 12:56:03 IST"
export const fmtDTS = (iso) =>
  iso ? `${dtfIn(getUserTimezone(), { day: "2-digit", month: "short" }).format(D(iso))}, ${fmtHMS(iso)}` : "—";
// "05 Jun 2026 12:56:03 IST"
export const fmtDTFull = (iso) =>
  iso ? `${dtfIn(getUserTimezone(), { day: "2-digit", month: "short", year: "numeric" }).format(D(iso))} ${fmtHMS(iso)}` : "—";
// "06/05 12:56 IST" (MM/DD)
export const fmtMDHM = (iso) =>
  iso ? `${dtfIn(getUserTimezone(), { month: "2-digit", day: "2-digit" }, "en-US").format(D(iso))} ${fmtHM(iso)}` : "—";
// "2026-06-05" in user zone (for day grouping)
export const dateKey = (iso) =>
  iso ? dtfIn(getUserTimezone(), { year: "numeric", month: "2-digit", day: "2-digit" }, "en-CA").format(D(iso)) : "";
// "Today" / "Yesterday" / "Thu, Jun 5" in user zone
export const fmtDateLabel = (iso) => {
  if (!iso) return "";
  const k = dateKey(iso);
  if (k === dateKey(new Date())) return "Today";
  if (k === dateKey(new Date(Date.now() - 86400000))) return "Yesterday";
  return fmtDay(iso);
};
